import Vue from 'vue'
import { Component, Watch, Ref } from 'vue-property-decorator'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { CalendarEvent, CalenderEventType } from '@/interfaces/calendar-event'
import { Policy, PolicyStatus } from '@/modules/Policy/interfaces/policy'
import { ExpiringPolicyNumberOfDate } from '@/interfaces/expiring-policy-number-date'
import { ApolloResponse } from '@/interfaces/apollo'
import { showErrors } from '@/utils/snack'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppLoader from '@/components/AppLoader.vue'

import { getExpiringPolicyNumber, getExpiringPolicies, getBindingPolicies, getEffectivePolicies } from '@/modules/DashboardCalendar/gql/calendar.gql'
import CalendarDailyDialog from '../components/daily-dialog.vue'

@Component({
  components: {
    AppBreadcrumbs,
    CalendarDailyDialog,
    AppLoader
  }
})

export default class Calendar extends Vue {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Calendar'
    }
  ]

  isLoading = false
  showDialog = false
  calendarValue = new Date()

  pDialog = {
    show: false,
    title: ``,
    list: [] as Policy[],
    showTab: false,
    tabIndex: 0,
    stat: {
      totalPremium: 0,
      totalCommission: 0
    }
  }

  eventDate = ''

  events: CalendarEvent[] = []

  getEventColor (event:CalendarEvent) {
    return event.color
  }

  async getExpiringPolicyNumberRequest () {
    this.isLoading = true
    this.events = []
    const month = this.calendarValue.getMonth()
    const year = this.calendarValue.getFullYear()
    try {
      const res: ApolloResponse = await this.$apollo.query({
        query: getExpiringPolicyNumber,
        variables: {
          year,
          month
        }
      })
      this.isLoading = false
      if (!res.data) {
        showErrors(res.errors)
        return
      }
      const result = res.data.getExpiringPolicyNumber as ExpiringPolicyNumberOfDate[]
      const event: CalendarEvent[] = []
      result.map(item => {
        if (item.expiringNumberInThisDay) {
          event.push({
            type: CalenderEventType.EXPIRING_TODAY,
            name: `Expiring Today:  ${item.expiringNumberInThisDay}`,
            start: this.$moment(item.date, 'MM/DD/YYYY').format('YYYY-MM-DD'),
            color: '#889BD4'
          })
        }
        if (item.expiringNumberInAMonth) {
          event.push({
            type: CalenderEventType.EXPIRING_IN_A_MONTH,
            name: `Expiring 30 Days:  ${item.expiringNumberInAMonth}`,
            start: this.$moment(item.date, 'MM/DD/YYYY').format('YYYY-MM-DD'),
            color: '#309BFF'
          })
        }
        if (item.bindingNumberInThisDay) {
          event.push({
            type: CalenderEventType.BINDING_TODAY,
            name: `Binding Today:  ${item.bindingNumberInThisDay}`,
            start: this.$moment(item.date, 'MM/DD/YYYY').format('YYYY-MM-DD'),
            color: '#889BD4',
            otherParams: {
              totalPremium: item.totalPremiumOfBindingInThisDay,
              totalCommission: item.totalCommissionOfBindingInThisDay
            }
          })
        }
        if (item.effectiveNumberInThisDay) {
          event.push({
            type: CalenderEventType.EFFECTIVE_TODAY,
            name: `Effective Today:  ${item.effectiveNumberInThisDay}`,
            start: this.$moment(item.date, 'MM/DD/YYYY').format('YYYY-MM-DD'),
            color: '#309BFF',
            otherParams: {
              totalPremium: item.totalPremiumOfBindingInThisDay,
              totalCommission: item.totalCommissionOfBindingInThisDay
            }
          })
        }
        if (item.totalPremiumOfBindingInThisDay) {
          event.push({
            type: CalenderEventType.BINDING_TODAY,
            name: `Total Premium Today:  ${item.totalPremiumOfBindingInThisDay}`,
            start: this.$moment(item.date, 'MM/DD/YYYY').format('YYYY-MM-DD'),
            color: '#889BD4',
            otherParams: {
              totalPremium: item.totalPremiumOfBindingInThisDay,
              totalCommission: item.totalCommissionOfBindingInThisDay
            }
          })
        }
        if (item.totalCommissionOfBindingInThisDay) {
          event.push({
            type: CalenderEventType.BINDING_TODAY,
            name: `Total Commission Today:  ${item.totalCommissionOfBindingInThisDay}`,
            start: this.$moment(item.date, 'MM/DD/YYYY').format('YYYY-MM-DD'),
            color: '#309BFF',
            otherParams: {
              totalPremium: item.totalPremiumOfBindingInThisDay,
              totalCommission: item.totalCommissionOfBindingInThisDay
            }
          })
        }
      })
      this.events = event
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getOneDayExpiringPolicy () {
    try {
      const res: ApolloResponse = await this.$apollo.query({
        query: getExpiringPolicies,
        variables: {
          expirationStartDate: this.$moment.tz(this.eventDate, 'America/New_York').startOf('day').unix() + '',
          expirationEndDate: this.$moment.tz(this.eventDate, 'America/New_York').endOf('day').unix() + ''
        }
      })
      if (!res.data) {
        showErrors(res.errors)
        return
      }
      const result = res.data.getExpiringPolicies as Policy[]
      this.pDialog.list = result.filter(item => item.status !== PolicyStatus.DELETED)
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getThirtyDaysExpiringPolicy () {
    try {
      const res: ApolloResponse = await this.$apollo.query({
        query: getExpiringPolicies,
        variables: {
          expirationStartDate: this.$moment(this.eventDate).startOf('day').unix() + '',
          expirationEndDate: this.$moment(this.eventDate).add(30, 'day').subtract(1, 'day').endOf('day').unix() + ''
        }
      })
      if (!res.data) {
        showErrors(res.errors)
        return
      }
      const result = res.data.getExpiringPolicies as Policy[]
      this.pDialog.list = result.filter(item => item.status !== PolicyStatus.DELETED)
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getOneDayBindingPolicy () {
    try {
      const res: ApolloResponse = await this.$apollo.query({
        query: getBindingPolicies,
        variables: {
          bindStartDate: this.$moment.tz(this.eventDate, 'America/New_York').startOf('day').unix() + '',
          bindEndDate: this.$moment.tz(this.eventDate, 'America/New_York').endOf('day').unix() + ''
        }
      })
      if (!res.data) {
        showErrors(res.errors)
        return
      }
      const result = res.data.getBindingPolicies as Policy[]
      this.pDialog.list = result.filter(item => item.status !== PolicyStatus.DELETED)
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getOneDayEffectivePolicy () {
    try {
      const res: ApolloResponse = await this.$apollo.query({
        query: getEffectivePolicies,
        variables: {
          effectiveStartDate: this.$moment.tz(this.eventDate, 'America/New_York').startOf('day').unix() + '',
          effectiveEndDate: this.$moment.tz(this.eventDate, 'America/New_York').endOf('day').unix() + ''
        }
      })
      if (!res.data) {
        showErrors(res.errors)
        return
      }
      const result = res.data.getEffectivePolicies as Policy[]
      this.pDialog.list = result.filter(item => item.status !== PolicyStatus.DELETED)
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  handlePrev () {
    this.calendarValue = this.$moment(this.calendarValue).subtract(1, 'month').toDate()
    this.getExpiringPolicyNumberRequest()
  }

  handleNext () {
    this.calendarValue = this.$moment(this.calendarValue).add(1, 'month').toDate()
    this.getExpiringPolicyNumberRequest()
  }

  handleEvent ({ event }: { event: CalendarEvent }) {
    this.eventDate = event.start as string
    this.pDialog = {
      show: true,
      title: this.$moment(event.start).format('MM/DD/YYYY'),
      list: [],
      showTab: true,
      tabIndex: 0,
      stat: {
        totalPremium: event.otherParams ? event.otherParams.totalPremium as number : 0,
        totalCommission: event.otherParams ? event.otherParams.totalCommission as number : 0
      }
    }

    switch (event.type) {
      case CalenderEventType.EXPIRING_TODAY:
        this.pDialog.tabIndex = 0
        return this.getOneDayExpiringPolicy()
      case CalenderEventType.EXPIRING_IN_A_MONTH:
        this.pDialog.tabIndex = 1
        return this.getThirtyDaysExpiringPolicy()
      case CalenderEventType.BINDING_TODAY:
        this.pDialog.tabIndex = 2
        return this.getOneDayBindingPolicy()
      case CalenderEventType.EFFECTIVE_TODAY:
        this.pDialog.tabIndex = 3
        return this.getOneDayEffectivePolicy()
      default:
    }
  }

  handleChangeTab (tabActive: number) {
    if (!this.pDialog.showTab) {
      return false
    }
    switch (tabActive) {
      case 0:
        this.pDialog.tabIndex = 0
        return this.getOneDayExpiringPolicy()
      case 1:
        this.pDialog.tabIndex = 1
        return this.getThirtyDaysExpiringPolicy()
      case 2:
        this.pDialog.tabIndex = 2
        return this.getOneDayBindingPolicy()
      case 3:
        this.pDialog.tabIndex = 3
        return this.getOneDayEffectivePolicy()
      default:
    }
  }

  handleCancel () {
    this.pDialog.show = false
  }

  async mounted () {
    this.getExpiringPolicyNumberRequest()
  }
}
